import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from './Icon.js';

interface ChipBaseProps {
  children: ReactNode;
  icon?: IconName;
  /** Emphasis for a selected filter or an important source. */
  selected?: boolean;
  /** Router path: the chip becomes a link (an entity reference). */
  to?: string;
  /** Present: a dismiss control appears, at least 44px on touch and keyboard-operable. */
  onRemove?: () => void;
  /** Accessible name for the dismiss control, e.g. "Remove filter: status open". */
  removeLabel?: string;
  /** For a chip that toggles (a filter): a real button with aria-pressed. */
  onToggle?: () => void;
  className?: string;
  title?: string;
}

/**
 * The tones. `label` is the design system's optional label family (01-foundations §1.8; 03-components
 * §9): a category from a vocabulary the app does not fix — a project — drawn as a solid `--label-N`
 * fill with `--label-N-fg` text, where `slot` (1–8) is a stable hash of the record's id. The other
 * tones are the fixed statuses and the neutral chip.
 */
export type ChipProps = ChipBaseProps &
  (
    | { tone?: 'neutral' | 'accent' | 'warning' | 'danger'; slot?: undefined }
    | { tone: 'label'; slot: number }
  );

/** A filter, a selection or an entity reference (03-components §9). Chip sets wrap; see .cluster. */
export function Chip(props: ChipProps) {
  const { children, icon, selected = false, tone = 'neutral', to, onRemove, removeLabel, onToggle, className, title } = props;
  const slotClass = props.tone === 'label' ? `chip--label-${props.slot}` : '';
  const classes = ['chip', `chip--${tone}`, slotClass, selected ? 'is-selected' : '', className ?? ''].filter(Boolean).join(' ');
  const body = (
    <>
      {icon ? <Icon name={icon} size={14} /> : null}
      <span className="chip__label">{children}</span>
    </>
  );
  if (onToggle) {
    return (
      <button type="button" className={`${classes} chip--button hit-area`} aria-pressed={selected} onClick={onToggle} title={title}>
        {body}
      </button>
    );
  }
  if (to) {
    return (
      <span className={classes} title={title}>
        <Link to={to} className="chip__link hit-area">{body}</Link>
        {onRemove ? <RemoveButton onRemove={onRemove} label={removeLabel} /> : null}
      </span>
    );
  }
  return (
    <span className={classes} title={title}>
      {body}
      {onRemove ? <RemoveButton onRemove={onRemove} label={removeLabel} /> : null}
    </span>
  );
}

function RemoveButton({ onRemove, label }: { onRemove: () => void; label?: string }) {
  return (
    <button type="button" className="chip__remove hit-area" onClick={onRemove} aria-label={label ?? 'Remove'}>
      <Icon name="x" size={14} />
    </button>
  );
}
